'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import Cookies from 'js-cookie';
import getCookieDomain from '@repo/utils/getCookieDomain';
import {
  GLOBAL_LOCALES,
  useLocalizedNavigation,
  getLocalizedPagePath,
  getCurrentUrlLocalePrefix,
  LOCALIZED_CURRENCY,
} from '@repo/gatsby-localization-helpers';
import { LocaleCurrencyEnum } from '../models/LocaleCurrencyEnum';

const CURRENCY_COOKIE = 'currency';
const COUNTRY_CONFIRMED_COOKIE = 'country_confirmed';

export type TranslatedUrl = { lang: string; url: string };

export type LocaleContextType = {
  currency: LocaleCurrencyEnum;
  updateCurrency: (newCurrency: LocaleCurrencyEnum) => void;
  getCountryCode: () => string;
  countryPickerActive: boolean;
  setCountryPickerActive: React.Dispatch<React.SetStateAction<boolean>>;
  setCountryCookie: () => void;
  locale: string;
  enableLanguageSelector: boolean;
  setLocale: (locale: string) => void;
  showLocalePicker: () => void;
  hideLocalePicker: () => void;
  isLocaleInitialized: boolean;
};
// create locale context to store currency
const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const isBrowser = globalThis.window !== undefined;

const getActiveCurrency = (
  allowedCurrencies: LocaleCurrencyEnum[],
  urlLocale?: string | null
): LocaleCurrencyEnum => {
  // 1. Query param — explicit override (browser only)
  if (isBrowser) {
    const urlParams = new URLSearchParams(window.location.search);
    const queryCurrency = urlParams.get(CURRENCY_COOKIE)?.toUpperCase() as
      | LocaleCurrencyEnum
      | undefined;
    if (queryCurrency && allowedCurrencies.includes(queryCurrency)) {
      return queryCurrency;
    }
  }

  // 2. Locale from URL or page context — works during both SSR (urlLocale
  //    comes from currentPathLocale in the page context) and CSR (urlLocale
  //    comes from the browser URL). This must be evaluated on the server too,
  //    otherwise SSR defaults to USD while CSR resolves the real currency,
  //    causing a React hydration mismatch.
  if (urlLocale) {
    const localeCurrency = LOCALIZED_CURRENCY[urlLocale] as
      | LocaleCurrencyEnum
      | undefined;
    if (localeCurrency && allowedCurrencies.includes(localeCurrency)) {
      return localeCurrency;
    }
    // URL has an explicit locale prefix that maps to a currency outside
    // allowedCurrencies (e.g., /ie → EUR when only [USD, CAD] are allowed).
    // The cookie must not override the URL's intent — fall to default.
    if (localeCurrency) {
      return LocaleCurrencyEnum.USD;
    }
  }

  // 3. Cookie — fallback for unprefixed URLs (browser only)
  if (isBrowser) {
    const storedCurrency = Cookies.get(CURRENCY_COOKIE)?.toUpperCase() as
      | LocaleCurrencyEnum
      | undefined;
    if (storedCurrency && allowedCurrencies.includes(storedCurrency)) {
      return storedCurrency;
    }
  }

  return LocaleCurrencyEnum.USD;
};

const hasCountryCookie = () => {
  if (!isBrowser) return false;

  const countryConfirmed = Cookies.get(COUNTRY_CONFIRMED_COOKIE);

  // Edge function writes 'false' on currency/path mismatch — must trigger picker
  if (countryConfirmed === 'false') return false;
  if (countryConfirmed === 'true') return true;

  // No explicit confirmation — currency cookie alone is sufficient signal
  const currency = Cookies.get(CURRENCY_COOKIE);
  return (
    currency === LocaleCurrencyEnum.USD || currency === LocaleCurrencyEnum.CAD
  );
};

const setCountryCookie = () => {
  if (isBrowser) {
    Cookies.set(COUNTRY_CONFIRMED_COOKIE, 'true', {
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      domain: getCookieDomain(),
    });
  }
};

// Returns the locale ID only when the URL has an explicit locale prefix.
// Returns null on root path, non-locale paths, or SSR — distinguishing
// "user intent encoded in URL" from "default fallback".
const getLocaleFromUrl = (): string | null => {
  if (!isBrowser) return null;

  const currentLocalePrefix = getCurrentUrlLocalePrefix();
  if (!currentLocalePrefix) return null;

  const urlLocale = currentLocalePrefix.substring(1);
  const matchingLocale = Object.values(GLOBAL_LOCALES).find(
    l => l.urlPath === urlLocale
  );

  return matchingLocale?.id ?? null;
};

const getStoredLocale = (): string =>
  getLocaleFromUrl() ?? GLOBAL_LOCALES.English.id;

const getPathWithoutLocale = (pathname: string): string => {
  if (!isBrowser) return pathname;

  const currentLocalePrefix = getCurrentUrlLocalePrefix();
  if (currentLocalePrefix) {
    return pathname.replace(currentLocalePrefix, '') || '/';
  }
  return pathname;
};

export type LocaleProviderProps = {
  children: ReactNode;
  allowedCurrencies: LocaleCurrencyEnum[];
  enableLanguageSelector?: boolean;
  site: 'petsafe' | 'invisiblefence';
  translatedUrls?: TranslatedUrl[];
  /** Locale identifier for the current page path (e.g. 'en-ie', 'fr-ca').
   *  Derived from the page's build-time context, this lets the provider
   *  detect client-side navigations across locale boundaries and reset the
   *  currency when the destination locale falls outside `allowedCurrencies`
   */
  currentPathLocale?: string;
  /** Disable context-owned navigation when the consuming site handles locale
   * changes itself (for example, IFB's country picker).
   */
  navigateOnLocaleChange?: boolean;
  /** Full-load on locale change instead of client-side navigate. Default false. */
  hardReloadOnLocaleChange?: boolean;
};
// create locale provider to store currency
function LocaleProvider({
  children,
  allowedCurrencies,
  enableLanguageSelector = false,
  site,
  translatedUrls = [],
  currentPathLocale,
  navigateOnLocaleChange = true,
  hardReloadOnLocaleChange = false,
}: Readonly<LocaleProviderProps>) {
  const [countryPickerActive, setCountryPickerActive] = useState(false);
  const [locale, setLocaleState] = useState<string>(() => getStoredLocale());
  const [isLocaleInitialized, setIsLocaleInitialized] = useState(false);
  const navigate = useLocalizedNavigation(locale);
  const previousLocale = useRef<string>(locale);
  const translatedUrlsRef = useRef<TranslatedUrl[]>(translatedUrls);

  useEffect(() => {
    translatedUrlsRef.current = translatedUrls;
  }, [translatedUrls]);

  const [currency, setCurrency] = useState<LocaleCurrencyEnum>(() =>
    getActiveCurrency(
      allowedCurrencies,
      getLocaleFromUrl() ?? currentPathLocale
    )
  );
  const isInitialCurrencySync = useRef(true);

  useEffect(() => {
    if (!hasCountryCookie() && site === 'invisiblefence') {
      setCountryPickerActive(true);
    }
  }, [locale]);

  // Mark locale as initialized after first client-side render
  useEffect(() => {
    setIsLocaleInitialized(true);
  }, []);

  // Navigate to the new locale URL when locale changes (e.g., language selector)
  useEffect(() => {
    if (!isBrowser) return;

    if (locale === previousLocale.current) return;

    if (!navigateOnLocaleChange) {
      previousLocale.current = locale;
      return;
    }

    const currentPath = globalThis.location.pathname;
    const pathWithoutLocale = getPathWithoutLocale(currentPath);
    const newLocalizedPath = getLocalizedPagePath(pathWithoutLocale, locale);
    const getTargetPath = (targetLocale: string): string => {
      const translatedUrl = translatedUrlsRef.current.find(
        url => url.lang === targetLocale
      );

      if (translatedUrl) {
        try {
          return new URL(translatedUrl.url, globalThis.location.origin)
            .pathname;
        } catch {
          return newLocalizedPath;
        }
      }

      // Cannot show `newLocalizedPath` in this case because it will be generated
      // with the wrong locale prefix and the language will not match the user's selection.
      return getLocalizedPagePath('/', targetLocale);
    };

    const targetPath = getTargetPath(locale);

    if (targetPath !== currentPath) {
      const { search: searchParams, hash } = globalThis.location;
      const fullPath = `${targetPath}${searchParams}${hash}`;
      // Consumer-controlled: hard reload vs client navigate. `replace` keeps
      // the previous locale out of session history either way.
      if (hardReloadOnLocaleChange) {
        previousLocale.current = locale;
        globalThis.location.replace(fullPath);
        return;
      }
      navigate(fullPath, { replace: true });
    }

    previousLocale.current = locale;
  }, [locale, navigate, navigateOnLocaleChange, hardReloadOnLocaleChange]);

  const setLocale: LocaleContextType['setLocale'] = (newLocale: string) => {
    setLocaleState(newLocale);
  };

  const updateCurrency: LocaleContextType['updateCurrency'] = newCurrency => {
    if (
      allowedCurrencies.includes(
        newCurrency?.toUpperCase() as LocaleCurrencyEnum
      )
    ) {
      setCurrency(newCurrency);

      let newLocale: string;
      if (newCurrency === LocaleCurrencyEnum.USD) {
        newLocale = GLOBAL_LOCALES.English.id;
      } else if (newCurrency === LocaleCurrencyEnum.CAD) {
        // Preserve fr-ca if user is already on it; otherwise default to en-ca
        newLocale =
          locale === GLOBAL_LOCALES.FrenchCanada.id
            ? GLOBAL_LOCALES.FrenchCanada.id
            : GLOBAL_LOCALES.EnglishCanada.id;
      }

      if (newLocale) {
        setLocale(newLocale);
      }
    }
  };

  const getCountryCode: LocaleContextType['getCountryCode'] = () => {
    let countryCode = 'US';

    if (currency === LocaleCurrencyEnum.CAD) {
      countryCode = 'CA';
    }

    return countryCode;
  };

  const showLocalePicker: LocaleContextType['showLocalePicker'] = () => {
    setCountryPickerActive(true);
  };

  const hideLocalePicker: LocaleContextType['hideLocalePicker'] = () => {
    setCountryPickerActive(false);
  };

  // When the user navigates to a page whose locale maps to a currency outside
  // allowedCurrencies (e.g., /ie → EUR when only [USD, CAD] are allowed),
  // reset currency to the default so CartContext uses the correct Shopify store.
  // Pages whose locale maps to an allowed currency (US, Canada) are left alone
  // — the existing cookie-based flow handles them correctly.
  useEffect(() => {
    if (!isBrowser || !currentPathLocale) return;

    const localeCurrency = LOCALIZED_CURRENCY[currentPathLocale] as
      | LocaleCurrencyEnum
      | undefined;

    if (localeCurrency && !allowedCurrencies.includes(localeCurrency)) {
      if (currency !== LocaleCurrencyEnum.USD) {
        setCurrency(LocaleCurrencyEnum.USD);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPathLocale]);

  // Sync currency when locale changes (e.g., via language selector).
  // Skip on initial mount so the cookie-derived initial currency isn't overridden
  // by the URL-derived locale's currency.
  useEffect(() => {
    if (isInitialCurrencySync.current) {
      isInitialCurrencySync.current = false;
      return;
    }
    setCurrency(getActiveCurrency(allowedCurrencies, locale));
  }, [locale, allowedCurrencies]);

  // write currency to browser storage
  useEffect(() => {
    Cookies.set(CURRENCY_COOKIE, currency, {
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      domain: getCookieDomain(),
    });
  }, [currency]);

  const memoizedValue = useMemo(
    () => ({
      currency,
      updateCurrency,
      getCountryCode,
      countryPickerActive,
      setCountryPickerActive,
      setCountryCookie,
      locale,
      enableLanguageSelector,
      setLocale,
      showLocalePicker,
      hideLocalePicker,
      isLocaleInitialized,
    }),
    [
      currency,
      countryPickerActive,
      locale,
      enableLanguageSelector,
      isLocaleInitialized,
    ]
  );

  // return locale context provider
  return (
    <LocaleContext.Provider value={memoizedValue}>
      {children}
    </LocaleContext.Provider>
  );
}

export { LocaleProvider, LocaleContext };

export const useLocaleContext = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocaleContext must be used within a LocaleProvider');
  }
  return context;
};
